import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  UnauthorizedError,
  type AgentLeadInfo,
  type WhatsAppChat,
  type WhatsAppMessage,
} from '../api';

type Props = {
  onSessionExpired: () => void;
  isAdmin?: boolean;
};

// Evolution doesn't push to the browser (websockets/pusher are off in the VM
// config), so the UI stays fresh by polling while the tab is visible.
const CHATS_POLL_MS = 15_000;
const MESSAGES_POLL_MS = 8_000;
// ~18 MB of binary once base64-decoded. UI gate; backend repeats the check.
const MAX_UPLOAD_BYTES = 18 * 1024 * 1024;

export function WhatsAppChats({ onSessionExpired, isAdmin = false }: Props) {
  const [chats, setChats] = useState<WhatsAppChat[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [filter, setFilter] = useState('');
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadInfo, setLeadInfo] = useState<AgentLeadInfo | null>(null);
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState<string | null>(null);
  // Botão de pause/resume da Roberta — `null` enquanto a request está no ar
  // pra desabilitar duplo-clique. Erro mostrado no header.
  const [pauseToggling, setPauseToggling] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);
  // Reset da sessão de teste — apaga a memória da Roberta nesse chat.
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetOk, setResetOk] = useState(false);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  const loadChats = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoadingChats(true);
      setError(null);
      return Promise.all([api.whatsappChats(), api.whatsappStatus().catch(() => null)])
        .then(([r, s]) => {
          setChats(r.data);
          setStatus(s?.data?.state ?? '');
          if (r.data.length > 0 && !selectedRef.current) {
            setSelected(r.data[0].remoteJid);
          }
        })
        .catch((e) => {
          if (e instanceof UnauthorizedError) onSessionExpired();
          else if (!opts?.silent) setError(String(e.message || e));
        })
        .finally(() => {
          if (!opts?.silent) setLoadingChats(false);
        });
    },
    [onSessionExpired]
  );

  const loadMessages = useCallback(
    (jid: string, opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoadingMsgs(true);
      setError(null);
      return api
        .whatsappMessages(jid, 200)
        .then((r) => {
          if (selectedRef.current !== jid) return;
          setMessages(r.data);
        })
        .catch((e) => {
          if (e instanceof UnauthorizedError) onSessionExpired();
          else if (!opts?.silent) {
            setError(String(e.message || e));
            setMessages([]);
          }
        })
        .finally(() => {
          if (!opts?.silent) setLoadingMsgs(false);
        });
    },
    [onSessionExpired]
  );

  const loadLead = useCallback(
    (jid: string) => {
      setLeadLoading(true);
      setLeadError(null);
      return api
        .whatsappAgentLead(jid)
        .then((r) => {
          if (selectedRef.current !== jid) return;
          setLeadInfo(r.data);
        })
        .catch((e) => {
          if (e instanceof UnauthorizedError) onSessionExpired();
          else setLeadError(String(e?.message || e));
        })
        .finally(() => {
          setLeadLoading(false);
        });
    },
    [onSessionExpired]
  );

  const togglePause = useCallback(
    async (jid: string, nextPaused: boolean) => {
      setPauseToggling(true);
      setPauseError(null);
      try {
        const r = await api.whatsappAgentPause(jid, nextPaused);
        // Atualização otimista: mescla o novo estado no leadInfo carregado
        // pra UI refletir imediatamente sem aguardar o reload.
        setLeadInfo((prev) => {
          if (selectedRef.current !== jid) return prev;
          const base =
            prev ?? {
              remoteJid: r.data.remoteJid,
              stage: 'qualification' as const,
              leadData: {},
              createdAt: r.data.pausedAt ?? Math.floor(Date.now() / 1000),
              updatedAt: r.data.pausedAt ?? Math.floor(Date.now() / 1000),
              lastInboundAt: null,
              lastOutboundAt: null,
            };
          const leadData = { ...(base.leadData || {}) };
          if (nextPaused) {
            leadData.agent_paused = true;
            leadData.agent_paused_at = r.data.pausedAt ?? Math.floor(Date.now() / 1000);
            leadData.paused_manually = true;
          } else {
            delete leadData.agent_paused;
            delete leadData.agent_paused_at;
            delete leadData.paused_manually;
          }
          return { ...base, leadData };
        });
        // Reload em segundo plano garante consistência com o backend caso
        // haja escrita concorrente (ex: handoff_to_sales rodando em paralelo).
        await loadLead(jid);
      } catch (e: any) {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setPauseError(String(e?.message || e));
      } finally {
        setPauseToggling(false);
      }
    },
    [loadLead, onSessionExpired]
  );

  const resetSession = useCallback(
    async (jid: string) => {
      setResetting(true);
      setResetError(null);
      setResetOk(false);
      try {
        await api.whatsappAgentReset(jid);
        // A memória do agente foi zerada no backend — reflete na UI imediato.
        if (selectedRef.current === jid) {
          setLeadInfo(null);
          setLeadError(null);
          setPauseError(null);
          setResetOk(true);
          // Recarrega o lead (deve voltar vazio) e o histórico do WhatsApp.
          await Promise.all([loadLead(jid), loadMessages(jid, { silent: true })]);
        }
      } catch (e: any) {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setResetError(String(e?.message || e));
      } finally {
        setResetting(false);
      }
    },
    [loadLead, loadMessages, onSessionExpired]
  );

  // Initial load.
  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // Reset & (re)load lead info when the user picks a different chat.
  // O lead info abastece tanto o painel "Ver dados do lead" quanto o botão
  // Pausar/Retomar Roberta no header — então carregamos sempre que o chat
  // muda, não só quando o painel está aberto.
  useEffect(() => {
    setLeadInfo(null);
    setLeadError(null);
    setPauseError(null);
    setResetError(null);
    setResetOk(false);
    if (!selected) return;
    loadLead(selected);
  }, [selected, loadLead]);

  // When the user picks a new chat, fetch its history fresh.
  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return;
    }
    loadMessages(selected);
  }, [selected, loadMessages]);

  // Visibility-aware polling: only fires while the tab is in the foreground.
  // Both intervals reset when visibility flips so the first refresh after
  // tab focus is immediate, not "wait for next tick".
  useEffect(() => {
    let chatsTimer: number | undefined;
    let msgsTimer: number | undefined;

    const start = () => {
      stop();
      if (document.visibilityState !== 'visible') return;
      // Immediate refresh on visibility regain, then on interval.
      loadChats({ silent: true });
      if (selectedRef.current) loadMessages(selectedRef.current, { silent: true });
      chatsTimer = window.setInterval(() => loadChats({ silent: true }), CHATS_POLL_MS);
      msgsTimer = window.setInterval(() => {
        if (selectedRef.current) loadMessages(selectedRef.current, { silent: true });
      }, MESSAGES_POLL_MS);
    };
    const stop = () => {
      if (chatsTimer) window.clearInterval(chatsTimer);
      if (msgsTimer) window.clearInterval(msgsTimer);
      chatsTimer = undefined;
      msgsTimer = undefined;
    };

    const onVis = () => start();
    document.addEventListener('visibilitychange', onVis);
    start();
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
    };
  }, [loadChats, loadMessages]);

  const filteredChats = useMemo(() => {
    if (!filter.trim()) return chats;
    const q = filter.trim().toLowerCase();
    return chats.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.remoteJid.toLowerCase().includes(q) ||
        (c.lastMessage || '').toLowerCase().includes(q)
    );
  }, [chats, filter]);

  const selectedChat = useMemo(
    () => chats.find((c) => c.remoteJid === selected) || null,
    [chats, selected]
  );

  return (
    <div className="wa-wrap" data-testid="wa-chats">
      <aside className="wa-sidebar">
        <div className="wa-sidebar-head">
          <div className="wa-sidebar-title">
            Conversas
            <span className="wa-count">{chats.length}</span>
          </div>
          <button className="btn" onClick={() => loadChats()} title="Recarregar conversas">
            ↻
          </button>
        </div>
        <div className={`wa-status wa-status-${status || 'unknown'}`}>
          {statusLabel(status)}
        </div>
        <input
          className="wa-search"
          placeholder="Buscar conversa…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="wa-chat-list">
          {loadingChats && <div className="wa-empty">carregando…</div>}
          {!loadingChats && filteredChats.length === 0 && (
            <div className="wa-empty">
              {chats.length === 0
                ? 'Nenhuma conversa ainda. Conecte a instância no Manager da Evolution API.'
                : 'Nenhuma conversa bate com o filtro.'}
            </div>
          )}
          {!loadingChats &&
            filteredChats.map((c) => (
              <button
                key={c.remoteJid}
                className={`wa-chat-row ${selected === c.remoteJid ? 'active' : ''}`}
                onClick={() => setSelected(c.remoteJid)}
              >
                <div className="wa-avatar">
                  {c.profilePicUrl ? (
                    <img src={c.profilePicUrl} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    <span>{initials(c.name)}</span>
                  )}
                </div>
                <div className="wa-chat-meta">
                  <div className="wa-chat-head">
                    <span className="wa-chat-name" title={c.name}>
                      {c.name}
                      {c.isGroup && <span className="wa-tag-group">grupo</span>}
                    </span>
                    {c.lastMessageAt && (
                      <span className="wa-chat-time">{formatRelative(c.lastMessageAt)}</span>
                    )}
                  </div>
                  <div className="wa-chat-last">
                    {c.lastMessageFromMe && <span className="wa-me-dot">você: </span>}
                    {c.lastMessage || <span className="muted">(sem mensagens)</span>}
                    {c.unreadCount > 0 && (
                      <span className="wa-unread">{c.unreadCount}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
        </div>
      </aside>

      <section className="wa-thread">
        {!selectedChat && <div className="wa-empty">Selecione uma conversa à esquerda.</div>}
        {selectedChat && (
          <>
            <header className="wa-thread-head">
              <div className="wa-avatar wa-avatar-lg">
                {selectedChat.profilePicUrl ? (
                  <img src={selectedChat.profilePicUrl} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span>{initials(selectedChat.name)}</span>
                )}
              </div>
              <div>
                <div className="wa-thread-name">
                  {selectedChat.name}
                  {selectedChat.isGroup && <span className="wa-tag-group">grupo</span>}
                </div>
                <div className="wa-thread-sub">
                  <code>{selectedChat.remoteJid}</code>
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                  {messages.length} mensagem(ns)
                </span>
                <RobertaPauseButton
                  paused={Boolean(leadInfo?.leadData?.agent_paused)}
                  loading={pauseToggling}
                  onToggle={(next) => togglePause(selectedChat.remoteJid, next)}
                />
                {isAdmin && (
                  <ResetSessionButton
                    loading={resetting}
                    onReset={() => {
                      if (
                        !confirm(
                          'Limpar a conversa e começar uma nova sessão de teste?\n\n' +
                            'A Roberta vai esquecer TUDO deste chat (histórico + dados do ' +
                            'lead: e-mail, stage, inscrição, pause) e tratar a próxima ' +
                            'mensagem como um lead novo.\n\n' +
                            'As mensagens do WhatsApp continuam no aparelho — isso limpa só ' +
                            'a memória do agente. Ação irreversível.'
                        )
                      )
                        return;
                      resetSession(selectedChat.remoteJid);
                    }}
                  />
                )}
                <button
                  className={`btn ${leadOpen ? 'active' : ''}`}
                  onClick={() => setLeadOpen((v) => !v)}
                  title="Ver dados extraídos da conversa pelo agente"
                  data-testid="wa-lead-toggle"
                >
                  {leadOpen ? 'Ocultar lead' : 'Ver dados do lead'}
                </button>
              </div>
            </header>
            {pauseError && (
              <div className="error" style={{ margin: '4px 16px 0' }}>
                Falha ao alternar Roberta: {pauseError}
              </div>
            )}
            {resetError && (
              <div className="error" style={{ margin: '4px 16px 0' }}>
                Falha ao limpar a conversa: {resetError}
              </div>
            )}
            {resetOk && (
              <div
                style={{
                  margin: '4px 16px 0',
                  padding: '6px 10px',
                  background: '#e7f7ec',
                  border: '1px solid #a9e0ba',
                  borderRadius: 8,
                  color: '#1f6b38',
                  fontSize: 12,
                }}
                data-testid="wa-reset-ok"
              >
                Memória da Roberta zerada — a próxima mensagem deste contato entra como
                lead novo.
              </div>
            )}
            {leadOpen && (
              <LeadPanel
                info={leadInfo}
                loading={leadLoading}
                error={leadError}
                onReload={() => selectedChat && loadLead(selectedChat.remoteJid)}
                onClose={() => setLeadOpen(false)}
                paused={Boolean(leadInfo?.leadData?.agent_paused)}
                pauseLoading={pauseToggling}
                pauseError={pauseError}
                onTogglePause={(next) => togglePause(selectedChat.remoteJid, next)}
              />
            )}
            {error && <div className="error">{error}</div>}
            <div className="wa-thread-body">
              {loadingMsgs && <div className="wa-empty">carregando mensagens…</div>}
              {!loadingMsgs && messages.length === 0 && (
                <div className="wa-empty">Sem mensagens nesta conversa.</div>
              )}
              {!loadingMsgs &&
                messages.map((m) => (
                  <Bubble
                    key={m.id}
                    m={m}
                    onDelete={
                      m.fromMe
                        ? async () => {
                            if (!confirm('Apagar essa mensagem para todos?')) return;
                            try {
                              await api.whatsappDeleteMessage({
                                remoteJid: m.remoteJid,
                                id: m.id,
                                fromMe: true,
                                participant: m.participant,
                              });
                              await loadMessages(selectedChat.remoteJid, { silent: true });
                            } catch (e: any) {
                              if (e instanceof UnauthorizedError) onSessionExpired();
                              else setError(String(e?.message || e));
                            }
                          }
                        : undefined
                    }
                  />
                ))}
            </div>
            <Composer
              remoteJid={selectedChat.remoteJid}
              disabled={status !== 'open'}
              onSent={() => loadMessages(selectedChat.remoteJid, { silent: true })}
              onSessionExpired={onSessionExpired}
            />
          </>
        )}
      </section>
    </div>
  );
}

// ── Lead panel ────────────────────────────────────────────────────────────
// Mostra o estado persistido pelo agente Roberta — stage atual + cada campo
// que ela conseguiu extrair via `save_lead_info`. Renderiza só os campos
// preenchidos; o resto vai num bloco "ainda não capturado" no fim pra deixar
// claro o que falta.
const LEAD_FIELDS: Array<{
  key: keyof NonNullable<AgentLeadInfo['leadData']>;
  label: string;
}> = [
  { key: 'contact_name', label: 'Nome' },
  { key: 'role', label: 'Cargo' },
  { key: 'email', label: 'E-mail' },
  { key: 'phone', label: 'Telefone alternativo' },
  { key: 'company_name', label: 'Empresa' },
  { key: 'cnpj', label: 'CNPJ' },
  { key: 'company_size', label: 'Porte (funcionários)' },
  { key: 'lead_type', label: 'Tipo' },
  { key: 'interests', label: 'Interesses' },
  { key: 'training_offered', label: 'Treinamento oferecido' },
  { key: 'demo_offered', label: 'Demo oferecida' },
];

function LeadPanel({
  info,
  loading,
  error,
  onReload,
  onClose,
  paused,
  pauseLoading,
  pauseError,
  onTogglePause,
}: {
  info: AgentLeadInfo | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  onClose: () => void;
  paused: boolean;
  pauseLoading: boolean;
  pauseError: string | null;
  onTogglePause: (nextPaused: boolean) => void;
}) {
  const data = info?.leadData || {};
  const filled = LEAD_FIELDS.filter((f) => hasValue(data[f.key]));
  const missing = LEAD_FIELDS.filter((f) => !hasValue(data[f.key]));
  const inHandoff = info?.stage === 'seller_handoff';

  return (
    <div className="wa-lead-panel" data-testid="wa-lead-panel">
      <div className="wa-lead-head">
        <div className="wa-lead-title">Dados do lead (agente)</div>
        {info && (
          <span className={`wa-lead-stage wa-lead-stage-${info.stage}`}>
            {stageLabel(info.stage)}
          </span>
        )}
        <button className="btn-ghost" onClick={onReload} disabled={loading} title="Recarregar">
          ↻
        </button>
        <button className="btn-ghost" onClick={onClose} title="Fechar">
          ✕
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          padding: '10px 12px',
          margin: '0 12px 8px',
          background: paused ? '#fde2e2' : inHandoff ? '#fff7e0' : '#f3f0ff',
          border: `1px solid ${paused ? '#f0b4b4' : inHandoff ? '#f0d68a' : '#d9d2ff'}`,
          borderRadius: 8,
        }}
        data-testid="wa-lead-pause-block"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13 }}>Atendimento automático da Roberta</strong>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {paused
              ? 'pausado — humano responde manualmente'
              : 'ativo — Roberta responde automaticamente'}
          </span>
          <div style={{ marginLeft: 'auto' }}>
            <RobertaPauseButton
              paused={paused}
              loading={pauseLoading}
              onToggle={onTogglePause}
            />
          </div>
        </div>
        {inHandoff && !paused && (
          <div style={{ fontSize: 12, color: '#7a5a00' }}>
            Lead em handoff para o comercial. Roberta segue respondendo até você pausar.
          </div>
        )}
        {pauseError && (
          <div className="error" style={{ marginTop: 4 }}>
            Falha ao alternar Roberta: {pauseError}
          </div>
        )}
      </div>

      {loading && <div className="wa-lead-empty">carregando…</div>}
      {error && <div className="wa-lead-error">{error}</div>}
      {!loading && !error && !info && (
        <div className="wa-lead-empty">
          Sem registro do agente pra esta conversa ainda. Os campos vão aparecer aqui assim
          que Roberta extrair algo da conversa.
        </div>
      )}

      {!loading && info && (
        <>
          <dl className="wa-lead-fields">
            {filled.length === 0 && (
              <div className="wa-lead-empty">Nenhum campo capturado ainda.</div>
            )}
            {filled.map((f) => (
              <div key={f.key} className="wa-lead-row">
                <dt>{f.label}</dt>
                <dd>{formatLeadValue(f.key, data[f.key])}</dd>
              </div>
            ))}
          </dl>

          {missing.length > 0 && (
            <div className="wa-lead-missing">
              <div className="wa-lead-missing-title">Ainda não capturado</div>
              <div className="wa-lead-missing-list">
                {missing.map((f) => (
                  <span key={f.key} className="wa-lead-chip">
                    {f.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="wa-lead-foot">
            <span>criado: {formatFullTs(info.createdAt)}</span>
            <span>atualizado: {formatFullTs(info.updatedAt)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function hasValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function formatLeadValue(key: string, v: unknown): string {
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (key === 'lead_type') {
    if (v === 'pf') return 'PF (pessoa física)';
    if (v === 'pj') return 'PJ (empresa)';
    if (v === 'unknown') return 'indefinido';
  }
  return String(v);
}

function stageLabel(stage: string): string {
  switch (stage) {
    case 'qualification':
      return 'qualificação';
    case 'conversion':
      return 'conversão (PF)';
    case 'seller_handoff':
      return 'handoff vendedor';
    case 'closed':
      return 'encerrada';
    default:
      return stage;
  }
}

// Botão "Pausar/Retomar Roberta" — quando paused=true, Roberta para de
// responder automaticamente nesse chat (mensagens do lead continuam chegando,
// mas um humano assume). Visualmente vira "destrutivo" no estado pausado
// pra deixar claro que o auto-reply está desligado.
function RobertaPauseButton({
  paused,
  loading,
  onToggle,
}: {
  paused: boolean;
  loading: boolean;
  onToggle: (nextPaused: boolean) => void;
}) {
  const next = !paused;
  const label = paused ? '▶ Retomar Roberta' : '⏸ Pausar Roberta';
  const title = paused
    ? 'Roberta está pausada nesse chat — clique pra reativar o atendimento automático.'
    : 'Pausa Roberta nesse chat — humano assume a partir das próximas mensagens.';
  return (
    <button
      className="btn"
      onClick={() => onToggle(next)}
      disabled={loading}
      title={title}
      data-testid="wa-roberta-toggle"
      style={
        paused
          ? {
              background: '#fde2e2',
              borderColor: '#f0b4b4',
              color: '#7a1f1f',
              fontWeight: 600,
            }
          : undefined
      }
    >
      {loading ? '…' : label}
    </button>
  );
}

// Botão "Nova sessão de teste" — admin-only. Zera a memória da Roberta nesse
// chat (histórico + dados do lead) pra recomeçar um teste do zero. Destrutivo,
// então sempre passa por um confirm() antes de chamar o backend.
function ResetSessionButton({
  loading,
  onReset,
}: {
  loading: boolean;
  onReset: () => void;
}) {
  return (
    <button
      className="btn"
      onClick={onReset}
      disabled={loading}
      title="Apaga a memória da Roberta nesse chat (histórico + dados do lead) pra começar uma nova sessão de teste. Não apaga as mensagens no WhatsApp."
      data-testid="wa-reset-session"
    >
      {loading ? '…' : '↺ Nova sessão'}
    </button>
  );
}

function Bubble({ m, onDelete }: { m: WhatsAppMessage; onDelete?: () => void }) {
  const cls = `wa-bubble ${m.fromMe ? 'me' : 'them'}`;
  return (
    <div className={cls}>
      {onDelete && (
        <button
          className="wa-bubble-delete"
          onClick={onDelete}
          title="Apagar mensagem para todos"
          aria-label="Apagar mensagem"
        >
          🗑
        </button>
      )}
      {m.participant && !m.fromMe && (
        <div className="wa-bubble-author">{m.pushName || formatJid(m.participant)}</div>
      )}
      <div className="wa-bubble-body">
        {m.body || <span className="muted">[{m.type}]</span>}
      </div>
      <div className="wa-bubble-meta">
        {formatFullTs(m.timestamp)}
        {m.fromMe && m.status && <span> · {m.status.toLowerCase()}</span>}
      </div>
    </div>
  );
}

// ── Composer ──────────────────────────────────────────────────────────────
// Sends text, files (image/video/document via sendMedia), and recorded audio
// (sendWhatsAppAudio). Each send triggers a silent reload of the thread so
// the bubble appears once Evolution writes it back.

type ComposerProps = {
  remoteJid: string;
  disabled: boolean;
  onSent: () => void;
  onSessionExpired: () => void;
};

type AttachPreview = {
  file: File;
  mediatype: 'image' | 'video' | 'document';
  objectUrl?: string;
};

function Composer({ remoteJid, disabled, onSent, onSessionExpired }: ComposerProps) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attach, setAttach] = useState<AttachPreview | null>(null);
  const [recState, setRecState] = useState<'idle' | 'recording' | 'sending'>('idle');
  const [recSeconds, setRecSeconds] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recStreamRef = useRef<MediaStream | null>(null);
  const recTimerRef = useRef<number | null>(null);

  // Reset transient state when the user switches chat.
  useEffect(() => {
    setText('');
    setError(null);
    if (attach?.objectUrl) URL.revokeObjectURL(attach.objectUrl);
    setAttach(null);
    cancelRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteJid]);

  function pickFile() {
    fileInputRef.current?.click();
  }

  function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-pick of same file
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(`Arquivo grande demais (>${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB).`);
      return;
    }
    const mediatype: AttachPreview['mediatype'] = f.type.startsWith('image/')
      ? 'image'
      : f.type.startsWith('video/')
      ? 'video'
      : 'document';
    const objectUrl =
      mediatype === 'image' || mediatype === 'video' ? URL.createObjectURL(f) : undefined;
    setError(null);
    setAttach({ file: f, mediatype, objectUrl });
  }

  function clearAttach() {
    if (attach?.objectUrl) URL.revokeObjectURL(attach.objectUrl);
    setAttach(null);
  }

  async function sendText() {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.whatsappSendText(remoteJid, t);
      setText('');
      onSent();
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e?.message || e));
    } finally {
      setSending(false);
    }
  }

  async function sendAttach() {
    if (!attach || sending) return;
    setSending(true);
    setError(null);
    try {
      const base64 = await fileToBase64(attach.file);
      const caption = text.trim() || undefined;
      await api.whatsappSendMedia({
        remoteJid,
        mediatype: attach.mediatype,
        mimetype: attach.file.type || 'application/octet-stream',
        media: base64,
        fileName: attach.file.name,
        caption,
      });
      setText('');
      clearAttach();
      onSent();
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e?.message || e));
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (attach) sendAttach();
      else sendText();
    }
  }

  async function startRecording() {
    if (recState !== 'idle') return;
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Gravação de áudio não suportada neste navegador.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const mime = pickAudioMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recChunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recChunksRef.current.push(ev.data);
      };
      rec.onstop = handleRecorderStop;
      recorderRef.current = rec;
      rec.start();
      setRecState('recording');
      setRecSeconds(0);
      recTimerRef.current = window.setInterval(() => {
        setRecSeconds((s) => s + 1);
      }, 1000);
    } catch (e: any) {
      setError(e?.message || 'Falha ao iniciar gravação');
    }
  }

  function stopRecording() {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    }
    if (recTimerRef.current) {
      window.clearInterval(recTimerRef.current);
      recTimerRef.current = null;
    }
  }

  function cancelRecording() {
    stopRecording();
    teardownStream();
    recChunksRef.current = [];
    setRecState('idle');
    setRecSeconds(0);
  }

  function teardownStream() {
    recStreamRef.current?.getTracks().forEach((t) => t.stop());
    recStreamRef.current = null;
    recorderRef.current = null;
  }

  async function handleRecorderStop() {
    const chunks = recChunksRef.current;
    teardownStream();
    if (chunks.length === 0) {
      setRecState('idle');
      setRecSeconds(0);
      return;
    }
    const mime = chunks[0].type || 'audio/webm';
    const blob = new Blob(chunks, { type: mime });
    recChunksRef.current = [];

    if (blob.size > MAX_UPLOAD_BYTES) {
      setError('Áudio grande demais.');
      setRecState('idle');
      setRecSeconds(0);
      return;
    }

    setRecState('sending');
    try {
      const base64 = await blobToBase64(blob);
      await api.whatsappSendAudio(remoteJid, base64);
      onSent();
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e?.message || e));
    } finally {
      setRecState('idle');
      setRecSeconds(0);
    }
  }

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (attach?.objectUrl) URL.revokeObjectURL(attach.objectUrl);
      teardownStream();
      if (recTimerRef.current) window.clearInterval(recTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recording = recState === 'recording';
  const busy = sending || recState === 'sending';
  const canSend =
    !busy && !recording && !disabled && (attach !== null || text.trim().length > 0);

  return (
    <div className="wa-composer">
      {disabled && (
        <div className="wa-composer-warn">
          Instância desconectada — reconecte no Manager da Evolution antes de enviar.
        </div>
      )}
      {error && <div className="wa-composer-error">{error}</div>}
      {attach && (
        <div className="wa-attach-preview">
          {attach.mediatype === 'image' && attach.objectUrl && (
            <img src={attach.objectUrl} alt={attach.file.name} />
          )}
          {attach.mediatype === 'video' && attach.objectUrl && (
            <video src={attach.objectUrl} controls />
          )}
          {attach.mediatype === 'document' && (
            <div className="wa-attach-doc">📎 {attach.file.name}</div>
          )}
          <div className="wa-attach-meta">
            <span className="wa-attach-name" title={attach.file.name}>
              {attach.file.name}
            </span>
            <span className="wa-attach-size">{humanSize(attach.file.size)}</span>
            <button className="btn-ghost" onClick={clearAttach} disabled={busy}>
              remover
            </button>
          </div>
        </div>
      )}

      <div className="wa-composer-row">
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={onFileChosen}
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
        />
        <button
          className="wa-icon-btn"
          onClick={pickFile}
          disabled={busy || recording || disabled}
          title="Anexar imagem, vídeo ou arquivo"
        >
          📎
        </button>

        {recording ? (
          <div className="wa-rec-row">
            <span className="wa-rec-dot" />
            <span className="wa-rec-time">{formatDuration(recSeconds)}</span>
            <button
              className="btn-ghost"
              onClick={() => {
                stopRecording();
                cancelRecording();
              }}
              title="Cancelar gravação"
            >
              cancelar
            </button>
            <button
              className="wa-icon-btn wa-icon-btn-send"
              onClick={stopRecording}
              title="Enviar áudio"
            >
              ✓
            </button>
          </div>
        ) : (
          <>
            <textarea
              className="wa-composer-text"
              placeholder={
                attach
                  ? 'Legenda (opcional)…'
                  : disabled
                  ? 'Conecte a instância pra enviar…'
                  : 'Digite uma mensagem… (Enter envia, Shift+Enter quebra linha)'
              }
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy || disabled}
              rows={1}
            />
            <button
              className="wa-icon-btn"
              onClick={startRecording}
              disabled={busy || disabled}
              title="Gravar áudio"
              aria-label="Gravar áudio"
            >
              <MicIcon />
            </button>
            <button
              className="wa-icon-btn wa-icon-btn-send"
              onClick={() => (attach ? sendAttach() : sendText())}
              disabled={!canSend}
              title={attach ? 'Enviar anexo' : 'Enviar mensagem'}
            >
              {busy ? '…' : '➤'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MicIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function pickAudioMime(): string | undefined {
  // Prefer ogg/opus (whatsapp native). Safari falls back to mp4/aac.
  const candidates = [
    'audio/ogg;codecs=opus',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return undefined;
}

function fileToBase64(file: File): Promise<string> {
  return blobToBase64(file);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const i = result.indexOf('base64,');
      resolve(i >= 0 ? result.slice(i + 'base64,'.length) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('file_read_failed'));
    reader.readAsDataURL(blob);
  });
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function statusLabel(state: string): string {
  if (!state) return 'estado desconhecido';
  if (state === 'open') return 'conectado';
  if (state === 'close') return 'desconectado';
  if (state === 'connecting') return 'conectando…';
  return state;
}

function initials(name: string): string {
  return (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
}

function formatJid(jid: string): string {
  const num = jid.split('@')[0];
  return num ? `+${num}` : jid;
}

function formatRelative(ts: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - ts;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h`;
  const d = new Date(ts * 1000);
  if (diff < 7 * 86_400)
    return d.toLocaleDateString('pt-BR', { weekday: 'short' });
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatFullTs(ts: number): string {
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}
